import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '@/features/auth/store/authStore';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private maxReconnectAttempts = 5;

  async connect(hubUrl: string): Promise<void> {
    const token = useAuthStore.getState().token;
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token || '',
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
          }
          return null; // Stop reconnecting
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onreconnecting(() => {
      console.log('SignalR: Reconnecting...');
    });

    this.connection.onreconnected(() => {
      console.log('SignalR: Reconnected');
    });

    this.connection.onclose((error) => {
      console.error('SignalR: Connection closed', error);
    });

    await this.connection.start();
    console.log('SignalR: Connected');
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  on(eventName: string, callback: (...args: any[]) => void): void {
    this.connection?.on(eventName, callback);
  }

  off(eventName: string, callback: (...args: any[]) => void): void {
    this.connection?.off(eventName, callback);
  }

  async invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
    if (!this.connection) {
      throw new Error('SignalR connection not established');
    }
    return await this.connection.invoke<T>(methodName, ...args);
  }

  get state(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }
}

export const signalRService = new SignalRService();
